import { FC, useEffect, useState } from 'react';
import styles from './styles.module.scss';
import { Wrapper as PendingWrapper } from 'components';
import { AntCard } from 'components-antd';
import classNames from 'classnames';
import { Icons } from 'pages/Radar/Icons';
import { priceConverter } from 'helpers';
import moment from 'moment';
import { AreaChart } from 'components/AreaChart';

interface PortfolioWidgetProps {
  title?: string;
  color?: string;
  homeValueMonthlyStats?: any;
  homeValueGrowth?: any;
  isLoading?: boolean;
}

export const PortfolioWidget: FC<PortfolioWidgetProps> = ({
  title,
  color = '',
  homeValueMonthlyStats,
  homeValueGrowth,
  isLoading = false,
}) => {
  const [count, setCount] = useState<string | number>('-');
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    const attomData = homeValueMonthlyStats?.attom || [];
    const hcData = homeValueMonthlyStats?.housecanary || [];

    const monthMap = new Map<string, { attom?: number; hc?: number }>();

    attomData.forEach(({ month, total }) => {
      if (!monthMap.has(month)) {
        monthMap.set(month, {});
      }
      if (total || total === 0) {
        monthMap.get(month)!.attom = total;
      }
    });

    hcData.forEach(({ month, total }) => {
      if (!monthMap.has(month)) {
        monthMap.set(month, {});
      }
      if (total || total === 0) {
        monthMap.get(month)!.hc = total;
      }
    });

    const sortedMonths = Array.from(monthMap.keys()).sort((a, b) => moment(a).diff(moment(b)));

    const averagedData = sortedMonths.map((month) => {
      const { attom, hc } = monthMap.get(month)!;
      let value: number | null = null;

      if (attom != null && hc != null) {
        value = Math.max(attom, hc);
      } else if (attom != null) {
        value = attom;
      } else if (hc != null) {
        value = hc;
      }

      return value;
    });

    const adjustedAveragedData =
      averagedData.length === 1 ? [averagedData[0], averagedData[0]] : averagedData;

    setData([
      {
        name: 'Average Home Value',
        data: adjustedAveragedData,
      },
    ]);

    const latestMonth = sortedMonths[sortedMonths.length - 1];
    const latest = monthMap.get(latestMonth);

    if (latest) {
      const { attom, hc } = latest;

      if (attom != null && hc != null) {
        const maxValue = Math.max(attom, hc);
        setCount(`${priceConverter(maxValue)}`);
      } else if (attom != null) {
        setCount(priceConverter(attom));
      } else if (hc != null) {
        setCount(priceConverter(hc));
      } else {
        setCount('-');
      }
    } else {
      setCount('-');
    }
  }, [homeValueMonthlyStats, homeValueGrowth]);

  return (
    <AntCard className={styles.card}>
      <PendingWrapper spinnerClassname={styles.centerSpinner} isPending={isLoading}>
        <header className={styles.widgetHeader}>
          <p className={styles.heading}>{title}</p>
        </header>

        <div className={styles.countContainer}>
          <span className={classNames(styles.count, styles[color])}>{count}</span>

          {data && (
            <div className={styles.chart}>
              <AreaChart
                curve="smooth"
                height={70}
                chartSeries={data}
                options={{
                  xaxis: {
                    labels: {
                      show: false,
                    },
                  },
                }}
                colors={['#6cd5f1']}
              />{' '}
            </div>
          )}
        </div>
      </PendingWrapper>
    </AntCard>
  );
};
